import { TodoItem, TodoList, TodoListGroup } from "./types";
import {
	apiCreateGroup,
	apiCreateItem,
	apiCreateList,
	apiDeleteGroup,
	apiDeleteItems,
	apiDeleteList,
	apiSetListItemsSort,
	apiSetListsSort,
	apiUpdateGroup,
	apiUpdateItem,
	apiUpdateList,
	SortEntry,
} from "./api.ts";
import { queueState } from "../states.ts";

export type QueueList = {
	type: "list";
	action: "create" | "update" | "delete";
	value: TodoList;
};

export type QueueGroup = {
	type: "group";
	action: "create" | "update" | "delete";
	value: TodoListGroup;
};

export type QueueItem = {
	type: "item";
	action: "create" | "update";
	value: TodoItem;
	listId: string;
};

export type QueueItemsDelete = {
	type: "item";
	action: "delete";
	value: TodoItem[];
	listId: string;
};

export type QueueSort = {
	type: "item" | "list";
	action: "sort";
	value: SortEntry[];
	listId?: string;
};

export type QueueEntry = QueueList | QueueGroup | QueueItem | QueueSort | QueueItemsDelete;

export const addToQueue = (entry: QueueEntry) => {
	queueState.push(entry);
};

export const isQueueSort = (value: QueueEntry): value is QueueSort => {
	return value.action == "sort";
};

export const isQueueList = (value: QueueEntry): value is QueueList => {
	return value.type == "list";
};

export const isQueueGroup = (value: QueueEntry): value is QueueGroup => {
	return value.type == "group";
};

export const isQueueItem = (value: QueueEntry): value is QueueItem => {
	return value.type == "item" && !!value.listId;
};

export const isQueueDeleteItems = (value: QueueEntry): value is QueueItemsDelete => {
	return value.type == "item" && !!value.listId && value.action == "delete";
};

export const processQueueCreate = async (entry: QueueEntry) => {
	if (isQueueList(entry)) {
		await apiCreateList(entry.value);
	}

	if (isQueueGroup(entry)) {
		await apiCreateGroup(entry.value);
	}

	if (isQueueItem(entry)) {
		const listId = entry.listId;
		if (!listId) {
			console.error("No list id for queue entry");
			return;
		}
		await apiCreateItem(listId, entry.value);
	}
};

export const processQueueDelete = async (entry: QueueEntry) => {
	if (isQueueList(entry)) {
		await apiDeleteList(entry.value.id);
	}
	if (isQueueGroup(entry)) {
		await apiDeleteGroup(entry.value.id);
	}
	if (isQueueDeleteItems(entry)) {
		const listId = entry.listId;
		if (!listId) {
			console.error("No list id for queue entry");
			return;
		}
		await apiDeleteItems(
			entry.listId,
			entry.value.map((item) => item.id),
		);
	}
};

export const processQueueUpdate = async (entry: QueueEntry) => {
	if (isQueueList(entry)) {
		await apiUpdateList(entry.value);
	}
	if (isQueueGroup(entry)) {
		await apiUpdateGroup(entry.value);
	}
	// updates only need the item id, so don't require listId here
	if (entry.type === "item" && entry.action === "update") {
		await apiUpdateItem(entry.value);
	}
};

export const processQueueSort = async (entry: QueueEntry) => {
	if (!isQueueSort(entry)) return;
	if (entry.type === "item") {
		if (!entry.listId) {
			console.error("No list id for queue sort");
			return;
		}
		await apiSetListItemsSort(entry.listId, entry.value);
	} else {
		await apiSetListsSort(entry.value);
	}
};

const RETRY_BASE_MS = 2000;
const RETRY_MAX_MS = 30_000;

let flushing: Promise<void> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let retryDelay = RETRY_BASE_MS;

const clearRetry = () => {
	if (retryTimer !== undefined) clearTimeout(retryTimer);
	retryTimer = undefined;
};

const scheduleRetry = () => {
	if (retryTimer !== undefined) return;
	retryTimer = setTimeout(() => {
		retryTimer = undefined;
		processQueue();
	}, retryDelay);
	retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS);
};

// 4xx (except timeout/rate limit) won't succeed on replay; keep the queue moving.
const isPermanentError = (e: any) => {
	const status = e?.response?.status;
	return typeof status === "number" && status >= 400 && status < 500 && status !== 408 && status !== 429;
};

const flushQueue = async () => {
	while (queueState.length > 0) {
		const entry = queueState[0];
		if (!entry) break;

		try {
			switch (entry.action) {
				case "create":
					await processQueueCreate(entry);
					break;
				case "delete":
					await processQueueDelete(entry);
					break;
				case "update":
					await processQueueUpdate(entry);
					break;
				case "sort":
					await processQueueSort(entry);
					break;
			}
			retryDelay = RETRY_BASE_MS;
		} catch (e) {
			console.error(e);
			if (!isPermanentError(e)) {
				// Keep the entry at the head so ordering (create before update) is preserved.
				scheduleRetry();
				return;
			}
			retryDelay = RETRY_BASE_MS;
		}

		queueState.shift();
	}
};

export const processQueue = async () => {
	if (flushing) {
		return flushing;
	}
	if (!navigator.onLine || queueState.length <= 0) {
		return;
	}

	clearRetry();
	flushing = flushQueue();
	try {
		await flushing;
	} finally {
		flushing = null;
	}
};
